import Autoplay from 'embla-carousel-autoplay'
import { EmblaCarousel } from '@percent/lemonade'
import { AppStoreCard } from '@percent/cause-dashboard/app/productOffers/AppStoreCard/AppStoreCard'
import {
  areCallsAcceptedFromCountry,
  isOfferAllowedInCountry
} from '@percent/cause-dashboard/common/utility/productOffers/productOffers'
import { productOffers } from '@percent/cause-dashboard/app/productOffers/data/productOffers'
import { useRef, useState } from 'react'
import { useCalendlyEventListener } from 'react-calendly'
import { useAuthState, useMutation } from '@percent/cause-dashboard/common/hooks'
import { useServices } from '@percent/cause-dashboard/context/serviceContext/ServiceContext'
import styles from './OffersCarousel.module.scss'
import { ProductOffer } from '@percent/cause-dashboard/app/productOffers/data/data.types'
import { AuthStateType } from '@percent/cause-dashboard/context/auth/authContext/AuthContext.types'

const hootSuiteHomeCarouselTile = {
  ...productOffers.hootsuite,
  primaryButton: {
    ...productOffers.hootsuite.primaryButton,
    link: 'https://www.hootsuite.com/pages/landing/non-profit-discount-application-percent?utm_campaign=hootgiving&utm_medium=content_syndication&utm_source=sponsored&utm_partner=goodstack&utm_vertical=non-profit&utm_content=may_homepage_tile'
  }
}

const docusignHomeCarouselTile = {
  ...productOffers.docusign,
  primaryButton: {
    ...productOffers.docusign.primaryButton,
    link: 'https://ecom.docusign.com/nonprofit/plans/iam?utm_campaign=NAM_US_DXP_AWA_2505_Goodstack&utm_content=marketplace-homepage&utm_medium=partner&utm_source=goodstack'
  }
}

export const allowedBookCallOffers: ProductOffer[] = [
  productOffers.adobeExpress,
  productOffers.adobeAcrobatPro,
  productOffers.atlassian,
  productOffers.claude,
  hootSuiteHomeCarouselTile,
  docusignHomeCarouselTile
]

export const restOffers: ProductOffer[] = [
  productOffers.adobeExpress,
  productOffers.adobeAcrobatPro,
  productOffers.atlassian,
  productOffers.claude,
  productOffers.nordlayer,
  hootSuiteHomeCarouselTile,
  docusignHomeCarouselTile
]

const getDisplayOffers = (authState: AuthStateType): ProductOffer[] => {
  const baseOffers = areCallsAcceptedFromCountry(authState) ? allowedBookCallOffers : restOffers
  return baseOffers.filter(offer => isOfferAllowedInCountry(authState, offer.allowedCountries))
}

const AUTOPLAY_DELAY = 3000

export const OffersCarousel = () => {
  const { authState } = useAuthState()
  const [selectedOfferName, setSelectedOfferName] = useState<string | undefined>(undefined)
  const { calendlyService } = useServices()
  const [{ isLoading: isCalendlyServiceLoading }, { apiFunc: saveMeetingLink }] = useMutation(
    calendlyService.saveMeetingLink
  )

  const autoplay = useRef(Autoplay({ delay: AUTOPLAY_DELAY }))

  // Copy pasta from ProductOffersPage
  // TODO: find a way to encapsulate this calendly thing inside of AppStoreCard
  useCalendlyEventListener({
    onEventScheduled: event => {
      const uri = event.data.payload.event.uri
      if (uri && !isCalendlyServiceLoading) {
        saveMeetingLink({
          accountId: authState.user?.id,
          organisationId: authState.user?.organisationId ?? '',
          email: authState.user?.email,
          source: selectedOfferName ?? 'app-store-offer',
          meetingLink: uri
        })
      }
      setSelectedOfferName(undefined)
    }
  })

  const offersToDisplay = getDisplayOffers(authState)

  const items = offersToDisplay.map((card, index) => ({
    id: `${card.name}-${index}`,
    content: (
      <div className={styles.carouselItem}>
        <AppStoreCard
          {...card}
          category="dashboard"
          selectOffer={setSelectedOfferName}
          selectedOfferName={selectedOfferName}
          tracking={{ name: card.tracking, category: 'dashboard' }}
        />
      </div>
    )
  }))

  return (
    <EmblaCarousel
      withIndicators
      draggable={false}
      height={360}
      initialSlide={1} // Set initial slide to 1 to make sure second one is in the center initially
      slideSize={{ base: '100%', sm: '50%', md: '33.333%' }}
      items={items}
      plugins={[autoplay.current]}
      className={styles.carousel}
      classNames={{
        container: styles.carouselContainer,
        indicators: styles.carouselIndicators,
        indicator: styles.carouselIndicator
      }}
    />
  )
}
